import { ScheduleClient } from "@formant/core/client/ScheduleClient";
import { defined } from "@formant/core/common/defined";
import { ISchedule } from "@formant/core/model/ISchedule";
import { ISchedulesQuery } from "@formant/core/model/ISchedulesQuery";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class ScheduleStore {
    private cache = new StoreCache<Uuid, ISchedule | false>();
    private listCache = new StoreCache<void, ISchedule[]>();

    constructor(
        private scheduleClient: ScheduleClient,
        private authStore: IAuthStore,
    ) {}

    public list(): ISchedule[] | undefined {
        return this.listCache.get(undefined, async () => this.fetchList());
    }

    public get(id: Uuid): ISchedule | false | undefined {
        return this.cache.get(id, async () => await this.fetchGet(id));
    }

    public async fetchGet(id: Uuid): Promise<ISchedule | false> {
        const schedule = await this.scheduleClient.get(
            await this.authStore.token(),
            id,
        );
        const value = schedule || false;
        this.cache.set(id, value);
        return value;
    }

    public async fetchList(): Promise<ISchedule[]> {
        const query: ISchedulesQuery = {
            organizationId: this.authStore.organizationId,
        };
        const results = await this.scheduleClient.query(
            await this.authStore.token(),
            query,
        );
        results.forEach(schedule => this.cache.set(schedule.id, schedule));
        this.listCache.set(undefined, results);
        return results;
    }

    public async save(schedule: ISchedule) {
        let saved: ISchedule | undefined;
        if (schedule.id === undefined) {
            saved = await this.create(schedule);
        } else {
            saved = await this.update(schedule.id, schedule);
        }
        return saved;
    }

    public async create(schedule: ISchedule): Promise<ISchedule> {
        const created = await this.scheduleClient.create(
            await this.authStore.token(),
            {
                ...schedule,
                organizationId: defined(this.authStore.organizationId),
            },
        );
        this.cache.clear();
        this.listCache.clear();

        return created;
    }

    public async update(id: Uuid, schedule: ISchedule) {
        const updated = await this.scheduleClient.update(
            await this.authStore.token(),
            id,
            schedule,
        );

        this.cache.clear();
        this.listCache.clear();
        return updated;
    }

    public async delete(id: Uuid) {
        await this.scheduleClient.delete(await this.authStore.token(), id);
        this.cache.clear();
        this.listCache.clear();
    }
}
